import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { api, apiErrorMessage } from '../../services/api'
import { Icon } from '../../components/ui/icons'

/**
 * Confirmar la hora desde el portal (pedido de Chejo, minuta 15-09). Vive en
 * su propio componente porque aparece en dos pantallas —"Mis citas" y
 * "Próximos controles"— y ambas deben refrescar exactamente lo mismo: el
 * estado de la cita, el control que la agendó y los contadores del header.
 *
 * Solo se ofrece donde tiene sentido. Una cita ya confirmada muestra el visto
 * en vez del botón, y una que ya pasó por recepción (llegó, en box, atendida)
 * no muestra nada: confirmar ahí no significaría nada.
 */

/** Estados desde los que el paciente puede confirmar. */
const CONFIRMABLE = ['REQUESTED', 'SCHEDULED']

export function ConfirmAppointmentButton({
  appointmentId,
  status,
  startsAt,
}: {
  appointmentId: string
  status: string
  /** Para no ofrecer confirmar una hora que ya pasó. */
  startsAt: string
}) {
  const queryClient = useQueryClient()
  const [error, setError] = useState('')

  const confirm = useMutation({
    mutationFn: async () => (await api.post(`/patient-portal/appointments/${appointmentId}/confirm`)).data,
    onSuccess: () => {
      setError('')
      for (const key of ['patient-appointments', 'patient-follow-ups', 'patient-summary']) {
        void queryClient.invalidateQueries({ queryKey: [key] })
      }
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  if (status === 'CONFIRMED') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
        <Icon name="check" className="h-4 w-4" />
        Cita confirmada
      </span>
    )
  }
  if (!CONFIRMABLE.includes(status) || startsAt < new Date().toISOString()) return null

  return (
    <div className="text-right">
      <button
        onClick={() => confirm.mutate()}
        disabled={confirm.isPending}
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white transition-colors',
          confirm.isPending ? 'opacity-60' : 'hover:bg-brand-700',
        )}
      >
        <Icon name="scheduleCheck" className="h-4 w-4" />
        {confirm.isPending ? 'Confirmando…' : 'Confirmar cita'}
      </button>
      {error && <p className="mt-1 text-xs text-red-700">{error}</p>}
    </div>
  )
}
